import { useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Paperclip, Upload } from "lucide-react";
import { encounterQueryKeys, encountersApi } from "../../shared/api/encounters";
import type { EncounterAttachment } from "../../shared/types/encounter";
import { formatDateTime } from "../../shared/format";
import { EncounterShell } from "./components/EncounterShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORY_LABEL: Record<EncounterAttachment["category"], string> = {
  LAB_RESULT: "Kết quả xét nghiệm",
  IMAGING: "Phim chụp",
  REFERRAL: "Giấy giới thiệu",
  CONSENT: "Đồng thuận",
  OTHER: "Khác",
};

/**
 * Lab/imaging attachment list. Real binary upload via M11 object storage:
 * presign → browser PUTs the bytes straight to S3 → complete. Downloads are
 * short-lived presigned GETs streamed through an authenticated API redirect.
 */
export function EncounterAttachmentsPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/encounters" replace />;

  return (
    <EncounterShell encounterId={id}>
      {(e) => (
        <AttachmentsPanel
          encounterId={id}
          items={e.attachments}
          readOnly={e.status !== "IN_PROGRESS"}
        />
      )}
    </EncounterShell>
  );
}

function AttachmentsPanel({
  encounterId,
  items,
  readOnly,
}: {
  encounterId: string;
  items: EncounterAttachment[];
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<EncounterAttachment["category"]>("LAB_RESULT");

  const upload = useMutation({
    mutationFn: (file: File) =>
      encountersApi.uploadAttachment(encounterId, file, category),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(encounterId) }),
  });

  const setVisible = useMutation({
    mutationFn: ({ att, visible }: { att: EncounterAttachment; visible: boolean }) =>
      encountersApi.setAttachmentPatientVisible(encounterId, att.id, visible),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(encounterId) }),
  });

  const download = useMutation({
    mutationFn: (att: EncounterAttachment) =>
      encountersApi.downloadAttachment(encounterId, att.id, att.fileName),
  });

  const remove = useMutation({
    mutationFn: (attachmentId: string) =>
      encountersApi.removeAttachment(encounterId, attachmentId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(encounterId) }),
  });

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <CardTitle className="text-lg">Đính kèm ({items.length})</CardTitle>
        {!readOnly && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select
              value={category}
              onValueChange={(v) =>
                setCategory(v as EncounterAttachment["category"])
              }
            >
              <SelectTrigger className="w-full sm:w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(CATEGORY_LABEL) as EncounterAttachment["category"][]).map((c) => (
                  <SelectItem key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending}
            >
              <Upload aria-hidden="true" />
              {upload.isPending ? "Đang tải lên…" : "Tải lên"}
            </Button>
          </div>
        )}
      </CardHeader>

      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
        {items.length === 0 ? (
          <div className="flex items-start gap-3 rounded-md border border-dashed border-input bg-muted/40 p-4">
            <Paperclip
              className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <div className="space-y-1">
              <strong className="text-sm font-semibold text-foreground">
                Chưa có tệp đính kèm.
              </strong>
              <p className="text-sm text-muted-foreground">
                Tải lên kết quả xét nghiệm ngoài (PDF) hoặc phim chụp (ảnh) để
                lưu cùng lần khám. Tối đa 200MB / tệp.
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-border rounded-md border border-border">
            {items.map((att) => (
              <div
                key={att.id}
                className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                    <FileText
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <span className="break-all">{att.fileName}</span>
                    <Badge variant="muted">{CATEGORY_LABEL[att.category]}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {(att.sizeBytes / 1024).toFixed(0)} KB · {att.mimeType}
                    {" · "}
                    {formatDateTime(att.uploadedAt)} bởi {att.uploadedBy}
                  </div>
                  {att.patientVisible && (
                    <Badge className="mt-1">Bệnh nhân xem được</Badge>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {att.status === "PENDING" ? (
                    <Badge variant="muted">Đang xử lý…</Badge>
                  ) : (
                    att.downloadable && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => download.mutate(att)}
                        disabled={download.isPending}
                      >
                        {download.isPending ? "Đang tải…" : "Tải xuống"}
                      </Button>
                    )
                  )}
                  {/* Mặc định đóng; mở từng tệp có chủ ý (#18735). */}
                  <Button
                    type="button"
                    variant={att.patientVisible ? "secondary" : "outline"}
                    size="sm"
                    onClick={() => setVisible.mutate({ att, visible: !att.patientVisible })}
                    disabled={setVisible.isPending}
                    title={
                      att.patientVisible
                        ? "Ẩn khỏi ứng dụng bệnh nhân"
                        : "Cho bệnh nhân xem tệp này trên ứng dụng"
                    }
                  >
                    {att.patientVisible ? "Ẩn với bệnh nhân" : "Cho bệnh nhân xem"}
                  </Button>
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Xoá tệp ${att.fileName}?`)) {
                          remove.mutate(att.id);
                        }
                      }}
                      disabled={remove.isPending}
                    >
                      Xoá
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      </CardContent>
    </Card>
  );
}
